<template>
  <div class="recipient-picker">
    <el-popover
      placement="bottom"
      width="240"
      trigger="manual"
      popper-class="kr-owner-popover"
      v-model="popoverVisible"
    >
      <div class="kr-owner-picker" @click.stop>
        <el-input
          v-model="search"
          size="mini"
          placeholder="Search team or member"
          prefix-icon="el-icon-search"
          class="kr-owner-search"
          clearable
        />
        <div v-if="filteredTeams.length > 0" class="kr-owner-teams-section">
          <div class="kr-owner-section-label">Teams</div>
          <div
            v-for="team in filteredTeams"
            :key="'team-' + team.key"
            class="kr-owner-item kr-owner-item--team"
            @click="selectTeam(team.key)"
          >
            <i class="el-icon-office-building" />
            <span class="kr-owner-item-name">{{ team.name }}</span>
          </div>
          <div class="kr-owner-section-label" style="margin-top: 4px;">People</div>
        </div>
        <div class="kr-owner-list">
          <div
            v-for="member in filteredMembers"
            :key="member.email"
            class="kr-owner-item"
            :class="{ 'kr-owner-item--selected': isSelected(member.email) }"
            @click="toggleMember(member.email)"
          >
            <div
              class="kr-owner-item-avatar"
              :style="{
                backgroundColor: member.house_color || '#ddd',
                backgroundImage: member.large_pic ? 'url(' + member.large_pic + ')' : 'none',
              }"
            >
              <span v-if="!member.large_pic">{{ getInitials(member.fullname) }}</span>
            </div>
            <span class="kr-owner-item-name">{{ member.fullname }}</span>
            <i v-if="isSelected(member.email)" class="el-icon-check kr-owner-item-check" />
          </div>
          <div v-if="filteredMembers.length === 0" class="kr-owner-empty">No members found</div>
        </div>
        <div v-if="value.length > 0" class="kr-owner-unassign" @click="clearAll">
          <i class="el-icon-close" /> Clear all
        </div>
      </div>
      <div slot="reference" class="recipient-trigger" @click.stop="popoverVisible = !popoverVisible">
        <template v-if="selectedMembers.length > 0">
          <div
            v-for="(owner, idx) in selectedMembers.slice(0, 3)"
            :key="owner.email"
            class="recipient-avatar"
            :style="{
              zIndex: 10 - idx,
              backgroundColor: owner.house_color || '#ddd',
              backgroundImage: owner.large_pic ? 'url(' + owner.large_pic + ')' : 'none',
            }"
          >
            <span v-if="!owner.large_pic">{{ getInitials(owner.fullname) }}</span>
          </div>
          <span v-if="selectedMembers.length > 3" class="recipient-extra">+{{ selectedMembers.length - 3 }}</span>
          <span class="recipient-summary">{{ summary }}</span>
        </template>
        <template v-else>
          <span class="recipient-placeholder">
            <i class="el-icon-plus" /> Add people
          </span>
        </template>
      </div>
    </el-popover>
  </div>
</template>

<script>
export default {
  name: 'AutomationRecipientPicker',
  props: {
    value: { type: Array, default: () => [] },
    teamId: { type: String, required: true },
  },
  data() {
    return {
      popoverVisible: false,
      search: '',
    }
  },
  mounted() {
    this._docHandler = (e) => {
      if (!this.popoverVisible) return
      const trigger = this.$el.querySelector('.recipient-trigger')
      if (trigger && trigger.contains(e.target)) return
      const poppers = document.querySelectorAll('.kr-owner-popover')
      for (let i = 0; i < poppers.length; i++) {
        if (poppers[i].contains(e.target)) return
      }
      this.popoverVisible = false
    }
    document.addEventListener('click', this._docHandler, true)
  },
  beforeDestroy() {
    if (this._docHandler) {
      document.removeEventListener('click', this._docHandler, true)
    }
  },
  computed: {
    teamMembers() {
      return this.$store.getters['goals/teamMembersForGoal'](this.teamId) || []
    },
    selectableTeams() {
      return this.$store.getters['goals/selectableTeamsForGoal'](this.teamId) || []
    },
    filteredTeams() {
      if (this.selectableTeams.length <= 1) return []
      if (!this.search) return this.selectableTeams
      const q = this.search.toLowerCase()
      return this.selectableTeams.filter(t => (t.name || '').toLowerCase().includes(q))
    },
    filteredMembers() {
      if (!this.search) return this.teamMembers
      const q = this.search.toLowerCase()
      return this.teamMembers.filter(m => (m.fullname || '').toLowerCase().includes(q))
    },
    selectedMembers() {
      const byEmail = new Map(this.teamMembers.map(m => [m.email, m]))
      return this.value
        .map(email => byEmail.get(email) || { email, fullname: email, large_pic: '', house_color: '#ddd' })
    },
    summary() {
      if (this.selectedMembers.length === 0) return ''
      if (this.selectedMembers.length === 1) return this.selectedMembers[0].fullname
      return `${this.selectedMembers.length} people`
    },
  },
  methods: {
    isSelected(email) {
      return this.value.includes(email)
    },
    toggleMember(email) {
      const next = this.value.includes(email)
        ? this.value.filter(e => e !== email)
        : [...this.value, email]
      this.$emit('input', next)
    },
    selectTeam(teamKey) {
      const members = this.$store.getters['goals/teamMembersForGoal'](teamKey) || []
      const set = new Set(this.value)
      for (const m of members) set.add(m.email)
      this.$emit('input', [...set])
    },
    clearAll() {
      this.popoverVisible = false
      this.search = ''
      this.$emit('input', [])
    },
    getInitials(name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      return (parts[0][0] || '?').toUpperCase()
    },
  },
}
</script>

<style scoped>
.recipient-picker {
  display: inline-block;
}

.recipient-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #e2e4ea;
  border-radius: 999px;
  cursor: pointer;
  background: white;
  transition: border-color 0.15s, background-color 0.15s;
}

.recipient-trigger:hover {
  border-color: #6c30cc;
  background: #fafbfc;
}

.recipient-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: white;
  margin-left: -6px;
  border: 2px solid white;
}

.recipient-avatar:first-child {
  margin-left: 0;
}

.recipient-extra {
  font-size: 11px;
  font-weight: 700;
  color: #6c30cc;
  margin-left: 2px;
}

.recipient-summary {
  font-size: 12px;
  font-weight: 500;
  color: #4a4a4a;
  margin-left: 4px;
}

.recipient-placeholder {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #8a8a8a;
}

/* Owner picker styles — mirror KeyResultRow.vue patterns so the popover feels native. */
.kr-owner-picker {
  width: 100%;
}

.kr-owner-search {
  margin-bottom: 8px;
}

.kr-owner-section-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8a8a;
  padding: 4px 6px;
}

.kr-owner-list {
  max-height: 220px;
  overflow-y: auto;
}

.kr-owner-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.kr-owner-item:hover {
  background: #fafbfc;
}

.kr-owner-item--team i {
  color: #909399;
  font-size: 16px;
  width: 24px;
  text-align: center;
}

.kr-owner-item--selected {
  background: #f2ecfb;
}

.kr-owner-item-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.kr-owner-item-name {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: #4a4a4a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kr-owner-item-check {
  color: #6c30cc;
  font-size: 14px;
}

.kr-owner-empty {
  font-size: 12px;
  color: #8a8a8a;
  text-align: center;
  padding: 12px;
}

.kr-owner-unassign {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 6px 0 6px;
  margin-top: 4px;
  border-top: 1px solid #e2e4ea;
  font-size: 12px;
  font-weight: 500;
  color: #cc3030;
  cursor: pointer;
}

.kr-owner-unassign:hover {
  color: #a52424;
}
</style>
